import {
  BR_FEATURE_CONFIG,
  loadBrSubmissionStatus,
} from '../../../constants/businessRegistration';
import { BusinessRegistrationService } from '../../../_services';
import {
  isAllowedBrFile,
  normalizeBrOcrPayload,
  evaluateBrScanResult,
  resolveBrSaveExpiryDate,
  resolveBrSaveIssueDate,
  resolveBrConfirmDatesFromOcr,
  unwrapOcrPayload,
} from '../utils/brOcr';
import {
  createEmptyBrConfirmForm,
} from '../utils/brConfirmSubmit';
import { refreshBrListsForDuplicateCheck } from '../utils/brDuplicatePendingRefresh';
import brDuplicatePendingGuard from './brDuplicatePendingGuard';

/**
 * Add Document：上传 / OCR / 确认提交（供应商与管理端代传共用）。
 * 依赖宿主：fromAdmin、targetEmpCompanyId、targetAccountId、submitType、
 * displayNameEn/Zh、currentStep、withTargetCompany（见页面）。
 */
export default {
  mixins: [brDuplicatePendingGuard],
  data() {
    return {
      selectedFile: null,
      selectedFileName: '',
      previewUrl: '',
      /** OCR 階段已上傳至 OSS 的 URL（BusinessLicenseImage），提交時直接複用 */
      ossMediaUrl: '',
      scanning: false,
      /** 扫描世代：丢弃过期 OCR 响应，避免连点覆盖 */
      scanGeneration: 0,
      saving: false,
      ocrRaw: null,
      /** 扫描告警与阻断同源（evaluateBrScanResult.alertKeys === blockerKeys） */
      scanBlockerKeys: [],
      confirmForm: createEmptyBrConfirmForm(),
      /** 每次扫描开始递增，确认表单触碰态重置 */
      scanResetToken: 0,
      showSubmitSuccess: false,
      /** 管理端提交前临时拉取的证件列表（查同号待审） */
      adminDocumentList: [],
      /** 管理端提交前临时拉取的更名待审（GetList 不含） */
      adminNameChangePending: [],
    };
  },
  beforeDestroy() {
    this.revokePreviewUrl();
  },
  methods: {
    triggerFileInput() {
      if (this.scanning) {
        return;
      }
      this.$refs.uploadSection?.triggerFileInput();
    },
    onFileChange(event) {
      const file = event.target.files && event.target.files[0];
      this.handleFile(file);
      if (event.target) {
        event.target.value = '';
      }
    },
    onDrop(event) {
      if (this.scanning) {
        return;
      }
      this.isDragOver = false;
      const file = event.dataTransfer.files && event.dataTransfer.files[0];
      this.handleFile(file);
    },
    revokePreviewUrl() {
      if (this.previewUrl && this.previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(this.previewUrl);
      }
      this.previewUrl = '';
    },
    async handleFile(file) {
      if (!file || this.scanning || this.saving) {
        return;
      }
      if (BR_FEATURE_CONFIG.minFileSizeBytes && file.size < BR_FEATURE_CONFIG.minFileSizeBytes) {
        this.notifyBrUploadError('file_size');
        return;
      }
      if (BR_FEATURE_CONFIG.maxFileSizeBytes && file.size > BR_FEATURE_CONFIG.maxFileSizeBytes) {
        this.notifyBrUploadError('file_size');
        return;
      }
      if (!isAllowedBrFile(file)) {
        this.notifyBrUploadError('file_type');
        return;
      }

      const stayOnConfirm = this.currentStep === 'confirm';
      this.revokePreviewUrl();
      this.selectedFile = file;
      this.selectedFileName = file.name;
      this.ossMediaUrl = '';
      this.previewUrl = URL.createObjectURL(file);
      this.ocrRaw = null;
      this.scanBlockerKeys = [];
      // 从确认页重新上传时保持 Step 2，不跳回上传页
      if (!stayOnConfirm) {
        this.currentStep = 'upload';
      }
      await this.scanFile(file);
    },
    /** 上传前置校验失败：仅上传区红框 + 红字（BrScanAlert），不弹框 */
    notifyBrUploadError(alertKey) {
      this.scanBlockerKeys = alertKey ? [alertKey] : [];
    },
    populateConfirmForm(normalized = {}) {
      const dates = resolveBrConfirmDatesFromOcr(normalized);
      this.confirmForm = {
        registeredCompanyName: normalized.companyName || '',
        registrationNo: normalized.registrationNo || '',
        hasExpirationDate: dates.hasExpirationDate,
        startDate: dates.startDate,
        expirationDate: dates.expirationDate,
      };
    },
    resetUploadState() {
      this.revokePreviewUrl();
      this.selectedFile = null;
      this.selectedFileName = '';
      this.ossMediaUrl = '';
      this.ocrRaw = null;
      this.scanBlockerKeys = [];
      this.confirmForm = createEmptyBrConfirmForm();
      this.currentStep = 'upload';
    },
    /** Step 2「点击重新上传」：不离开确认页，仅弹出选文件 */
    handleReupload() {
      if (this.scanning || this.saving) {
        return;
      }
      this.$refs.confirmReuploadInput?.click();
    },
    applyScanResult(evaluation, normalized = {}) {
      this.scanBlockerKeys = evaluation.blockerKeys || [];
      this.populateConfirmForm(normalized);
      if (evaluation.canProceed) {
        this.currentStep = 'confirm';
        return;
      }
      // 确认页重新上传失败：留在 Step 2 展示错误；首次上传失败仍回 Step 1
      if (this.currentStep !== 'confirm') {
        this.currentStep = 'upload';
      }
    },
    applyScanFailure(errorOrRes) {
      const evaluation = evaluateBrScanResult({}, {
        scanFailed: true,
        httpStatus: errorOrRes?.response?.status || errorOrRes?.httpStatus,
      });
      this.applyScanResult(evaluation, {});
    },
    async scanFile(file) {
      const generation = (this.scanGeneration = (this.scanGeneration || 0) + 1);
      this.scanning = true;
      this.scanBlockerKeys = [];
      this.scanResetToken += 1;
      this.confirmForm = createEmptyBrConfirmForm();
      try {
        const res = await BusinessRegistrationService.ScanBusinessLicense(file);
        if (generation !== this.scanGeneration) {
          return;
        }
        if (!res?.isSuccessful) {
          this.applyScanFailure(res);
          return;
        }

        const payload = unwrapOcrPayload(res?.data || {});
        this.ocrRaw = payload;
        const normalized = normalizeBrOcrPayload(payload);

        if (normalized.businessLicenseImage) {
          this.ossMediaUrl = normalized.businessLicenseImage;
          if (!this.previewUrl) {
            this.previewUrl = normalized.businessLicenseImage;
          }
        }

        const evaluation = evaluateBrScanResult(normalized, { alreadyNormalized: true });
        this.applyScanResult(evaluation, normalized);
      } catch (error) {
        if (generation !== this.scanGeneration) {
          return;
        }
        this.applyScanFailure(error);
      } finally {
        if (generation === this.scanGeneration) {
          this.scanning = false;
        }
      }
    },
    resolveOriginalFileName() {
      return String(
        this.selectedFileName
        || this.selectedFile?.name
        || '',
      ).trim();
    },
    buildSavePayload(formData) {
      const normalized = normalizeBrOcrPayload(this.ocrRaw || {});
      const originalFileName = this.resolveOriginalFileName();
      return this.withTargetCompany({
        companyName: formData.registeredCompanyName,
        registrationNo: formData.registrationNo,
        issueDate: resolveBrSaveIssueDate(formData),
        expiryDate: resolveBrSaveExpiryDate(formData),
        mediaFileName: this.ossMediaUrl,
        originalFileName,
        ocrPayloadJson: this.ocrRaw ? JSON.stringify(this.ocrRaw) : '',
        ocrCompanyName: normalized.companyName || '',
        ocrRegistrationNo: normalized.registrationNo || '',
        displayNameEn: (formData.displayNameEn || '').trim() || this.displayNameEn || '',
        displayNameZh: (formData.displayNameZh || '').trim() || this.displayNameZh || '',
      });
    },
    /** 提交失败对用户只展示友好文案，不直出 systemErrorMsg */
    notifySubmitFailed() {
      this.$toast?.error?.(this.$t('lbl_EMPBE_BR_Submit_Failed'));
    },
    async saveAndEnqueue(formData) {
      const saveRes = await BusinessRegistrationService.SaveDocument(this.buildSavePayload(formData));
      if (!saveRes?.isSuccessful) {
        throw new Error('BR_SUBMIT_FAILED');
      }
      const businessRegistrationId = Number(
        saveRes?.data?.businessRegistrationId
        || saveRes?.data?.BusinessRegistrationId,
      );
      if (!Number.isFinite(businessRegistrationId) || businessRegistrationId <= 0) {
        throw new Error('BR_SUBMIT_FAILED');
      }
      const enqueuePayload = this.withTargetCompany({
        businessRegistrationId,
      });
      const submitType = String(this.submitType || '').trim();
      if (submitType) {
        enqueuePayload.submitType = submitType;
      }
      const enqueueRes = await BusinessRegistrationService.EnqueueForApproval(enqueuePayload);
      if (!enqueueRes?.isSuccessful) {
        throw new Error('BR_SUBMIT_FAILED');
      }
    },
    async onSubmitSuccess() {
      if (this.fromAdmin) {
        this.$toast?.success?.(this.$t('lbl_EMPBE_BR_Submit_Success'));
        this.$emit('submitted');
        return;
      }
      await loadBrSubmissionStatus('hard');
      this.currentStep = 'pending';
      this.showSubmitSuccess = true;
    },
    closeSubmitSuccess() {
      this.showSubmitSuccess = false;
    },
    /**
     * 提交前强制刷新列表做同号校验。
     * @returns {{ ok: boolean, list: Array, nameChanges: Array }}
     */
    async refreshListsForDuplicateCheck() {
      const refreshed = await refreshBrListsForDuplicateCheck({
        fromAdmin: !!this.fromAdmin,
        empCompanyId: this.targetEmpCompanyId,
      });
      if (this.fromAdmin && refreshed.ok) {
        this.adminDocumentList = refreshed.list;
        this.adminNameChangePending = refreshed.nameChanges;
      }
      return refreshed;
    },
    isDuplicatePendingBusy() {
      return !!this.saving;
    },
    async executeDuplicatePendingSubmit(formData) {
      await this.executeSubmitForReview(formData);
    },
    async submitForReview(formData) {
      if (!this.selectedFile || this.scanBlockerKeys.length) {
        return;
      }
      if (!this.ossMediaUrl) {
        this.$toast?.error?.(this.$t('lbl_EMPBE_BR_Alert_ScanFailed'));
        return;
      }
      if (this.saving || this.showDuplicatePendingConfirm) {
        return;
      }
      // 同号检查 await 期间也占锁，避免连点重复提交；弹确认时在 finally 释放
      this.saving = true;
      try {
        const refreshed = await this.refreshListsForDuplicateCheck();
        if (!refreshed.ok) {
          this.$toast?.error?.(this.$t('lbl_EMPBE_BR_List_LoadFailed'));
          return;
        }
        if (this.hasPendingBrDuplicates(
          refreshed.list,
          formData?.registrationNo,
          refreshed.nameChanges,
        )) {
          this.openDuplicatePendingConfirm(formData);
          return;
        }
        await this.saveAndEnqueue(formData);
        await this.onSubmitSuccess();
      } catch (error) {
        this.notifySubmitFailed();
      } finally {
        this.saving = false;
      }
    },
    async executeSubmitForReview(formData) {
      if (this.saving) {
        return;
      }
      this.saving = true;
      try {
        await this.saveAndEnqueue(formData);
        await this.onSubmitSuccess();
      } catch (error) {
        this.notifySubmitFailed();
      } finally {
        this.saving = false;
      }
    },
  },
};
